/**
 * Sources data hooks — react-query v3 wrappers over the host-provided
 * `SourcesClient`. The lib never imports axios or fetch directly; every
 * network call goes through `useSourcesHost().client`.
 *
 * Lifted from `apps/aegix-aim-react/src/hooks/useSourceData.ts` and
 * decoupled from AIM's vendor service module. See ADR-057.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSourcesHost } from '../context/SourcesHostContext';
import type { IVendor, IVendorCreate } from '../types/vendor';

export const sourceQueryKeys = {
  all: ['sources'] as const,
  list: (params: any) => [...sourceQueryKeys.all, 'list', params] as const,
  alertTypes: (siteId: number) =>
    [...sourceQueryKeys.all, 'alertTypes', siteId] as const,
};

export function useSourceList(params: {
  school: number | null;
  query?: string;
  offset?: number;
  limit?: number;
  // The endpoint defaults an absent param to active-only, so "show
  // everything" must be requested explicitly with 'all'.
  deleted?: boolean | 'all';
}) {
  const { client } = useSourcesHost();
  const payload: Record<string, any> = {
    school: params.school ?? 0,
    query: params.query,
    offset: params.offset ?? 0,
    limit: params.limit ?? 100,
  };
  if (params.deleted !== undefined) {
    payload['deleted'] = params.deleted;
  }

  const query = useQuery({
  queryKey: sourceQueryKeys.list(payload),
  queryFn: () => client.listVendors(payload as IVendor),

      enabled: !!params.school,
      staleTime: 30000,
    
});

  return {
    sources: query.data?.results ?? [],
    next: query.data?.next ?? null,
    count: query.data?.count ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useSourceAlertTypes(siteId: number | null) {
  const { client } = useSourcesHost();
  const query = useQuery({
    queryKey: sourceQueryKeys.alertTypes(siteId ?? 0),
    // Settings → Sources is admin-only and must show the same alert types as
    // Settings → Types (which opts in via include_system=true). Backend
    // (ADR 100) gates the param on schools.change_alerttype, so non-admins
    // who land here still get the safe-default filtered list.
    queryFn: () =>
      client.listAlertTypes({
        school: siteId ?? undefined,
        limit: 200,
        offset: 0,
        deleted: false,
        include_system: true,
      }),
    enabled: !!siteId,
    staleTime: 60000,
  });

  return {
    alertTypes: query.data?.results ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useSourceMutations() {
  const { client } = useSourcesHost();
  const queryClient = useQueryClient();

  const invalidateSources = () => {
    queryClient.invalidateQueries({ queryKey: sourceQueryKeys.all });
    // Legacy AIM cache key kept for cross-host invalidation parity.
    queryClient.invalidateQueries({ queryKey: ['getVendorDetails'] });
  };

  const createSource = useMutation({
  mutationFn: (data: IVendorCreate) => client.createVendor(data),
 onSuccess: invalidateSources 
});

  const updateSource = useMutation({
  mutationFn: ({ id, data }: { id: string; data: IVendorCreate }) =>
      client.updateVendor(id, data),
 onSuccess: invalidateSources 
});

  const deleteSource = useMutation({
  mutationFn: (id: string) => client.deleteVendor(id),

    onSuccess: invalidateSources,
  
});

  return {
    createSource,
    updateSource,
    deleteSource,
    // v5: useMutation.isLoading was renamed to isPending.
    isCreating: createSource.isPending,
    isUpdating: updateSource.isPending,
    isDeleting: deleteSource.isPending,
    invalidateSources,
  };
}
